import React from "react";

/**
 * Error boundary applicatif.
 *
 * Sans ce composant, une seule erreur de rendu (ou un chunk lazy qui echoue a
 * charger) fait un ECRAN BLANC total, sans recuperation. Ici :
 *  - on capture l'erreur et on affiche un fallback lisible (style inline, donc
 *    fonctionne meme si le CSS de l'app n'a pas charge) ;
 *  - les erreurs de chargement de chunk (frequentes apres un deploiement qui
 *    invalide les anciens hashes de fichiers) declenchent UN rechargement
 *    automatique (une seule fois, pour eviter toute boucle) ;
 *  - on remonte l'erreur a la console et a Sentry s'il est present.
 */

function isChunkLoadError(error) {
  if (!error) return false;
  const text = `${error.name || ""} ${error.message || ""}`;
  return /ChunkLoadError|Loading chunk [\d]+ failed|Loading CSS chunk|dynamically imported module|Importing a module script failed/i.test(
    text
  );
}

const CHUNK_RELOAD_KEY = "__ss_chunk_reload_once__";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    try {
      // eslint-disable-next-line no-console
      console.error("[ErrorBoundary]", error, info && info.componentStack);
      if (typeof window !== "undefined" && window.Sentry && window.Sentry.captureException) {
        window.Sentry.captureException(error, {
          extra: { componentStack: info && info.componentStack },
        });
      }
    } catch (_) {
      /* noop : ne jamais relancer depuis le handler d'erreur */
    }

    // Apres un deploiement, les anciens chunks lazy n'existent plus -> rechargement
    // automatique unique (sessionStorage evite la boucle si l'erreur persiste).
    if (isChunkLoadError(error)) {
      try {
        if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
          window.location.reload();
        }
      } catch (_) {
        /* sessionStorage indisponible : on laisse le fallback s'afficher */
      }
    }
  }

  handleReload = () => {
    try {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    } catch (_) {
      /* noop */
    }
    window.location.reload();
  };

  handleRetry = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const chunk = isChunkLoadError(error);
    const isDev = typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production";

    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1.5rem",
          fontFamily:
            "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
          background: "#f8fafc",
          color: "#0f172a",
        }}
      >
        <div
          style={{
            maxWidth: 480,
            width: "100%",
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: 16,
            boxShadow: "0 10px 30px rgba(2, 6, 23, 0.08)",
            padding: "2rem",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 40, lineHeight: 1, marginBottom: "0.75rem" }} aria-hidden>
            {chunk ? "↻" : "⚠️"}
          </div>
          <h1 style={{ fontSize: "1.25rem", margin: "0 0 0.5rem" }}>
            {chunk ? "Une nouvelle version est disponible" : "Une erreur est survenue"}
          </h1>
          <p style={{ color: "#475569", margin: "0 0 1.5rem", lineHeight: 1.5 }}>
            {chunk
              ? "L'application a ete mise a jour. Recharge la page pour continuer."
              : "Quelque chose s'est mal passe lors de l'affichage de cette page. Tu peux reessayer ou recharger l'application."}
          </p>

          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={this.handleReload}
              style={{
                background: "#2563eb",
                color: "#fff",
                border: "none",
                borderRadius: 10,
                padding: "0.65rem 1.25rem",
                fontSize: "0.95rem",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Recharger la page
            </button>
            {!chunk && (
              <button
                type="button"
                onClick={this.handleRetry}
                style={{
                  background: "#fff",
                  color: "#0f172a",
                  border: "1px solid #cbd5e1",
                  borderRadius: 10,
                  padding: "0.65rem 1.25rem",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Reessayer
              </button>
            )}
          </div>

          {isDev && error && (
            <details style={{ marginTop: "1.5rem", textAlign: "left" }}>
              <summary style={{ cursor: "pointer", color: "#64748b" }}>Detail technique (dev)</summary>
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  background: "#0f172a",
                  color: "#e2e8f0",
                  padding: "0.75rem",
                  borderRadius: 8,
                  fontSize: "0.8rem",
                  marginTop: "0.5rem",
                  maxHeight: 220,
                  overflow: "auto",
                }}
              >
                {String(error && (error.stack || error.message || error))}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
